import { useEffect, useMemo } from 'react';
import { useCurrentUserApi, useClientApi } from '@hero/api-hooks';
import { resolveLoadPackingAndQuantityFromVehicles } from '@hero/core';
import {
  type ClientSettings,
  type OrgSettings,
  type ItemType,
  type ItemPacking,
  type VehicleTypes,
  type Org,
  type Client,
} from '@hero/schema';
import { type PublishOrderLoadDraft, usePublishOrderStore } from '@hero/hooks-shared';

/**
 * Phase 2 (relations-v2) — automatically apply order-defaults on the
 * publish-order page.
 *
 * What it does:
 *   - Reads the current user's Org from `useCurrentUserApi`
 *     (`AuthGetMeData.org` — the unified Org doc for the active org)
 *     — no need for a dedicated `useOrgApi` call, the auth `me`
 *     payload already carries the unified `Org` doc with `settings`.
 *   - Reads the picked CRM Client from `useClientApi(state.customer.customerId)`
 *     when one is selected.
 *   - Computes the **default load detail** to seed into the publish
 *     store, role-aware:
 *       - **External client selected** (any creator role):
 *         `{ ...orgSettings, ...clientSettings }` — client wins on
 *         every overlapping field.
 *       - **Internal shipment or no client**:
 *         Org settings only.
 *   - Pushes the result into the store via `applyLoadDefaults`. The
 *     store is sticky-aware: any field the user has explicitly
 *     touched (`loadOverrides`) is left alone.
 *
 * Mounted from `PublishOrderPage`. Returns nothing — the side effect
 * is the store update. The "save these settings as the default for
 * this client / org" prompt is a separate concern handled by
 * {@link useSaveOrderDefaultsManager}.
 */
export function useApplyClientOrderDefaultsManager(): void {
  const { data: me } = useCurrentUserApi();
  const customerId = usePublishOrderStore(s => s.customer?.customerId);
  const isInternalShipment = usePublishOrderStore(s => s.isInternalShipment);
  const lastApplied = usePublishOrderStore(s => s.loadAppliedSource);
  const applyLoadDefaults = usePublishOrderStore(s => s.applyLoadDefaults);

  // Only fetch the picked client when the user is publishing externally.
  const { data: client, isLoading: isClientLoading } = useClientApi(
    !isInternalShipment && customerId ? customerId : undefined,
  );

  const creatorOrg = me?.org;

  const defaults = useMemo<Partial<PublishOrderLoadDraft> | null>(() => {
    if (!creatorOrg) return null;
    return resolveLoadDefaults({
      orgSettings: creatorOrg.settings,
      clientSettings: !isInternalShipment ? client?.settings : undefined,
    });
  }, [creatorOrg, client?.settings, isInternalShipment]);

  useEffect(() => {
    if (!defaults) return;
    const orgId = creatorOrg?._id?.toString();
    const clientIdStr = !isInternalShipment ? customerId : undefined;
    // Wait for the client doc before seeding — otherwise we'd stamp org-only
    // defaults and mark the client as "applied" while settings are still in flight.
    if (clientIdStr && isClientLoading) return;
    if (
      lastApplied?.orgId === orgId &&
      lastApplied?.clientId === clientIdStr
    ) {
      return;
    }
    applyLoadDefaults({
      defaults,
      source: { orgId, clientId: clientIdStr },
    });
  }, [
    defaults,
    creatorOrg?._id,
    customerId,
    isInternalShipment,
    isClientLoading,
    lastApplied?.orgId,
    lastApplied?.clientId,
    applyLoadDefaults,
  ]);
}

/**
 * Pure resolver — given the creator role + org/client settings,
 * compute the publish-order `load` draft defaults.
 *
 * Exported for unit tests and for symmetric use on the backend
 * (the same merge rule lives in
 * `OrderFetcher.resolveVehiclesForCreate` for vehicle resolution; we
 * only port the FE side fully because items/temperature/etc. are
 * filled in optionally and never resolved server-side).
 */
export function resolveLoadDefaults(input: {
  orgSettings: OrgSettings | undefined;
  clientSettings: ClientSettings | undefined;
}): Partial<PublishOrderLoadDraft> {
  const { orgSettings, clientSettings } = input;

  const merged: Partial<PublishOrderLoadDraft> = {};

  const preferredVehicles =
    clientSettings?.vehicle_preferred_types ??
    orgSettings?.vehicle_preferred_types;
  if (preferredVehicles?.length) {
    merged.vehicleTypes = preferredVehicles as VehicleTypes[];
    merged.vehicleType = preferredVehicles[0] as VehicleTypes;
  }

  const itemType: ItemType | undefined =
    clientSettings?.item_default_type ?? orgSettings?.item_default_type;
  if (itemType) merged.itemType = itemType;

  const packing: ItemPacking | undefined =
    clientSettings?.item_default_packing ?? orgSettings?.item_default_packing;
  const quantity =
    clientSettings?.item_default_quantity ?? orgSettings?.item_default_quantity;

  if (packing) {
    merged.packing = packing;
  } else if (preferredVehicles?.length) {
    merged.packing = resolveLoadPackingAndQuantityFromVehicles(
      preferredVehicles as VehicleTypes[],
    ).packing;
  }

  if (typeof quantity === 'number') {
    merged.quantity = quantity;
  } else if (preferredVehicles?.length) {
    merged.quantity = resolveLoadPackingAndQuantityFromVehicles(
      preferredVehicles as VehicleTypes[],
    ).quantity;
  }

  const itemName = clientSettings?.item_default_name ?? orgSettings?.item_default_name;
  if (itemName) merged.itemDescription = itemName;

  const itemCost =
    clientSettings?.item_default_cost ?? orgSettings?.item_default_cost;
  if (typeof itemCost === 'number') merged.itemCost = itemCost;

  // Weight is a `Weight` shape on settings ({ value, type }) but the
  // publish-store stores raw tons (display unit shared with the
  // order drawer's edit-load surface). Convert kg → ton when needed
  // so older settings written before the unit unification still seed
  // a sensible default.
  const weight = clientSettings?.item_default_weight ?? orgSettings?.item_default_weight;
  if (weight) {
    const ton = weight.type === 'ton' ? weight.value : weight.value / 1000;
    if (ton > 0) merged.weightTon = ton;
  }

  const temperature =
    clientSettings?.item_default_temperature ?? orgSettings?.item_default_temperature;
  if (temperature?.types?.[0]) {
    merged.temperatureType = temperature.types[0];
    if (temperature.degree) merged.temperatureDegree = temperature.degree;
  }

  const podSettings =
    clientSettings?.pod_settings ?? orgSettings?.pod_settings;
  if (podSettings) merged.podSettings = podSettings;

  return merged;
}

/**
 * Re-export of the resolver inputs as a typed payload — useful for
 * tests and for places that want to build the defaults *without*
 * mounting the React effect (e.g. server-side rendering or unit
 * tests of the merge rule).
 */
export type ResolveLoadDefaultsInput = Parameters<typeof resolveLoadDefaults>[0];

/**
 * Convenience type re-exports so consumers don't have to dig into
 * `@hero/schema` directly to type their own callers.
 */
export type { Org, Client };
